import { motion } from 'framer-motion';
import { FileText, Tag, XCircle } from 'lucide-react';
import React from 'react';
import { Category } from './CategoryCard'; // Import the Category interface

interface CategoryModalProps {
  category: Category;
  onClose: () => void;
}

export const CategoryModal: React.FC<CategoryModalProps> = ({
  category,
  onClose,
}) => {
  const handleModalClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <motion.div
      className="lp-fixed lp-inset-0 lp-z-50 lp-overflow-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Backdrop */}
      <motion.div
        className="lp-fixed lp-inset-0 lp-bg-black/70 lp-backdrop-blur-sm"
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      />

      {/* Modal */}
      <motion.div
        className="lp-fixed lp-left-1/2 lp-top-1/2 -lp-translate-x-1/2 -lp-translate-y-1/2 lp-w-[90vw] md:lp-w-[600px]
          lp-bg-black/95 lp-border lp-border-[#DDB889]/10 lp-rounded-2xl lp-shadow-[0_0_15px_rgba(221,184,137,0.07)]"
        onClick={handleModalClick}
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
      >
        <div className="lp-p-6">
          <div className="lp-flex lp-flex-col lp-gap-4 lp-mb-6">
            <div className="lp-flex lp-justify-end">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onClose}
                className="lp-text-[#DDB889] hover:lp-text-white lp-transition-colors lp-duration-300"
              >
                <XCircle className="lp-w-6 lp-h-6" />
              </motion.button>
            </div>
            <div className="lp-flex lp-gap-2">
              <Tag className="lp-w-5 lp-h-5 lp-shrink-0 lp-text-[#DDB889] lp-mt-1" />
              <div className="lp-flex-1 lp-min-w-0">
                <h3 className="lp-text-xl lp-font-medium lp-text-[#DDB889] lp-break-words">
                  {category.name}
                </h3>
              </div>
            </div>
          </div>

          <div className="lp-space-y-6 lp-max-h-[60vh] lp-overflow-y-auto">
            {/* Category image if available */}
            {category.image && (
              <div className="lp-w-full lp-overflow-hidden lp-rounded-xl lp-mb-4">
                <img
                  src={category.image?.secure_url}
                  alt={category.name}
                  className="lp-w-full lp-h-auto lp-object-cover"
                />
              </div>
            )}

            <div className="lp-flex lp-gap-2">
              <FileText className="lp-w-5 lp-h-5 lp-shrink-0 lp-text-[#DDB889] lp-mt-1" />
              <div className="lp-flex-1 lp-min-w-0">
                <p className="lp-text-gray-300 lp-text-sm lp-leading-relaxed lp-break-words lp-whitespace-pre-wrap">
                  {category.description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CategoryModal;
